import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Plus, Pencil, Trash2, X, Save, ListVideo } from "lucide-react";
import { toast } from "sonner";
import { Field, Toggle } from "./AdminMovies";
import FileUpload from "../../components/FileUpload";
import VideoSourceSelector from "../../components/VideoSourceSelector";

const empty = {
  title: "", description: "", genre: "", category: "", releaseYear: 2025, rating: 0,
  posterUrl: "", bannerUrl: "", trailerUrl: "", cast: "", isFeatured: false, isKidsContent: false, totalSeasons: 1,
};

const emptyEp = { title: "", description: "", episodeNumber: 1, seasonNumber: 1, thumbnailUrl: "", videoUrl: "", videoSource: "direct", embedCode: "", duration: "" };

export default function AdminSeries() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [epManager, setEpManager] = useState(null);

  const load = () => api.get("/series").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const open = (s) => {
    setEdit(s ? { ...s, genre: (s.genre || []).join(", "), cast: (s.cast || []).join(", ") } : { ...empty });
  };

  const save = async () => {
    const payload = {
      ...edit,
      genre: edit.genre.split(",").map((s) => s.trim()).filter(Boolean),
      cast: edit.cast.split(",").map((s) => s.trim()).filter(Boolean),
      releaseYear: parseInt(edit.releaseYear) || 2025,
      rating: parseFloat(edit.rating) || 0,
      totalSeasons: parseInt(edit.totalSeasons) || 1,
    };
    if (edit.id) await api.put(`/admin/series/${edit.id}`, payload);
    else await api.post("/admin/series", payload);
    toast.success("Saved");
    setEdit(null);
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this series and all episodes?")) return;
    await api.delete(`/admin/series/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-series-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-black tracking-tighter" style={{ fontFamily: "Outfit" }}>TV Series</h1>
          <p className="text-[#888] text-sm">{items.length} shows in library</p>
        </div>
        <button onClick={() => open(null)} className="btn-primary" data-testid="add-series-btn"><Plus size={16} /> Add Series</button>
      </div>

      <div className="bg-[#0d0d0d] border border-white/5 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#080808] text-[#888] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-3">Poster</th><th className="text-left p-3">Title</th><th className="text-left p-3">Seasons</th><th className="text-left p-3">Year</th><th className="text-left p-3">Rating</th><th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-t border-white/5 hover:bg-white/5">
                <td className="p-3">{s.posterUrl && <img src={s.posterUrl} alt="" className="w-10 h-14 object-cover rounded" />}</td>
                <td className="p-3 font-medium">{s.title}</td>
                <td className="p-3 text-[#aaa]">{s.totalSeasons}</td>
                <td className="p-3 text-[#aaa]">{s.releaseYear}</td>
                <td className="p-3 text-[#aaa]">{s.rating?.toFixed(1)}</td>
                <td className="p-3 text-right">
                  <button onClick={() => setEpManager(s)} className="text-[#aaa] hover:text-white p-1" title="Episodes" data-testid={`episodes-${s.id}`}><ListVideo size={16} /></button>
                  <button onClick={() => open(s)} className="text-[#aaa] hover:text-white p-1 ml-1"><Pencil size={16} /></button>
                  <button onClick={() => del(s.id)} className="text-[#aaa] hover:text-[#E50914] p-1 ml-1"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edit && (
        <Modal title={edit.id ? "Edit Series" : "Add Series"} onClose={() => setEdit(null)} onSave={save}>
          <Field label="Title" value={edit.title} onChange={(v) => setEdit({ ...edit, title: v })} />
          <Field label="Description" value={edit.description} onChange={(v) => setEdit({ ...edit, description: v })} textarea />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Year" value={edit.releaseYear} onChange={(v) => setEdit({ ...edit, releaseYear: v })} type="number" />
            <Field label="Rating" value={edit.rating} onChange={(v) => setEdit({ ...edit, rating: v })} type="number" step="0.1" />
            <Field label="Total Seasons" value={edit.totalSeasons} onChange={(v) => setEdit({ ...edit, totalSeasons: v })} type="number" />
            <Field label="Category" value={edit.category} onChange={(v) => setEdit({ ...edit, category: v })} />
          </div>
          <Field label="Genres (comma)" value={edit.genre} onChange={(v) => setEdit({ ...edit, genre: v })} />
          <Field label="Cast (comma)" value={edit.cast} onChange={(v) => setEdit({ ...edit, cast: v })} />
          <FileUpload label="Poster" value={edit.posterUrl} onChange={(v) => setEdit({ ...edit, posterUrl: v })} accept="image/*" kind="image" />
          <FileUpload label="Banner (wide)" value={edit.bannerUrl} onChange={(v) => setEdit({ ...edit, bannerUrl: v })} accept="image/*" kind="image" />
          <Field label="Trailer URL" value={edit.trailerUrl} onChange={(v) => setEdit({ ...edit, trailerUrl: v })} />
          <div className="flex gap-6">
            <Toggle label="Featured" checked={edit.isFeatured} onChange={(v) => setEdit({ ...edit, isFeatured: v })} />
            <Toggle label="Kids-safe" checked={edit.isKidsContent} onChange={(v) => setEdit({ ...edit, isKidsContent: v })} />
          </div>
        </Modal>
      )}

      {epManager && <EpisodesManager series={epManager} onClose={() => setEpManager(null)} />}
    </div>
  );
}

function EpisodesManager({ series, onClose }) {
  const [episodes, setEpisodes] = useState([]);
  const [editEp, setEditEp] = useState(null);

  const load = () => api.get(`/series/${series.id}`).then((r) => setEpisodes(r.data.episodes || []));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    const payload = {
      ...editEp,
      episodeNumber: parseInt(editEp.episodeNumber) || 1,
      seasonNumber: parseInt(editEp.seasonNumber) || 1,
    };
    if (editEp.id) await api.put(`/admin/episodes/${editEp.id}`, payload);
    else await api.post(`/admin/series/${series.id}/episodes`, payload);
    toast.success("Episode saved");
    setEditEp(null);
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete episode?")) return;
    await api.delete(`/admin/episodes/${id}`);
    load();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="episodes-modal">
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-white/5 flex items-center justify-between sticky top-0 bg-[#0a0a0a]">
          <h2 className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>Episodes – {series.title}</h2>
          <div className="flex gap-2">
            <button onClick={() => setEditEp({ ...emptyEp })} className="btn-primary !py-1.5 !px-3 !text-xs"><Plus size={14} /> Add</button>
            <button onClick={onClose} className="text-[#aaa] hover:text-white"><X /></button>
          </div>
        </div>
        <div className="p-5">
          {episodes.length === 0 && <div className="text-[#888] text-center py-10">No episodes yet. Add the first one.</div>}
          <div className="space-y-2">
            {episodes.map((e) => (
              <div key={e.id} className="flex items-center gap-3 bg-[#0d0d0d] p-3 rounded-lg border border-white/5">
                <div className="text-xs text-[#888] font-mono w-12">S{e.seasonNumber}E{e.episodeNumber}</div>
                <div className="flex-1">
                  <div className="font-medium">{e.title}</div>
                  <div className="text-xs text-[#888]">{e.duration}</div>
                </div>
                <button onClick={() => setEditEp(e)} className="text-[#aaa] hover:text-white p-1"><Pencil size={14} /></button>
                <button onClick={() => del(e.id)} className="text-[#aaa] hover:text-[#E50914] p-1"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
      {editEp && (
        <Modal title={editEp.id ? "Edit Episode" : "Add Episode"} onClose={() => setEditEp(null)} onSave={save}>
          <Field label="Title" value={editEp.title} onChange={(v) => setEditEp({ ...editEp, title: v })} />
          <Field label="Description" value={editEp.description} onChange={(v) => setEditEp({ ...editEp, description: v })} textarea />
          <div className="grid grid-cols-3 gap-3">
            <Field label="Season" value={editEp.seasonNumber} onChange={(v) => setEditEp({ ...editEp, seasonNumber: v })} type="number" />
            <Field label="Episode #" value={editEp.episodeNumber} onChange={(v) => setEditEp({ ...editEp, episodeNumber: v })} type="number" />
            <Field label="Duration" value={editEp.duration} onChange={(v) => setEditEp({ ...editEp, duration: v })} />
          </div>
          <Field label="Thumbnail URL" value={editEp.thumbnailUrl} onChange={(v) => setEditEp({ ...editEp, thumbnailUrl: v })} />
          <FileUpload label="Or upload thumbnail" value={editEp.thumbnailUrl} onChange={(v) => setEditEp({ ...editEp, thumbnailUrl: v })} accept="image/*" kind="image" />
          <VideoSourceSelector
            source={editEp.videoSource}
            videoUrl={editEp.videoUrl}
            embedCode={editEp.embedCode}
            onChange={(p) => setEditEp({ ...editEp, ...p })}
          />
        </Modal>
      )}
    </div>
  );
}

export function Modal({ title, onClose, onSave, children }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4">
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-white/5 flex items-center justify-between sticky top-0 bg-[#0a0a0a]">
          <h2 className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>{title}</h2>
          <button onClick={onClose} className="text-[#aaa] hover:text-white"><X /></button>
        </div>
        <div className="p-6 space-y-4">{children}</div>
        <div className="p-5 border-t border-white/5 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary !py-2 !px-4">Cancel</button>
          <button onClick={onSave} className="btn-primary !py-2 !px-4" data-testid="modal-save-btn"><Save size={16} /> Save</button>
        </div>
      </div>
    </div>
  );
}
